import axios from 'axios';
import type { DateTime } from 'luxon';

export default class VideosService
{
    public static async getList(filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/cms/videos', {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`admin/cms/videos/${id}`)).data;
    }

    public static async create(model: UpsertModel)
    {
        return (await axios.post<Resource<FormModel>>(`admin/cms/videos`, model)).data;
    }

    public static async update(id: number, model: UpsertModel)
    {
        return (await axios.put<Resource<FormModel>>(`admin/cms/videos/${id}`, model)).data;
    }

    public static async remove(id: number)
    {
        return (await axios.delete(`admin/cms/videos/${id}`)).data as Statement;
    }

    public static async status(id: number, status: PublicationEnum)
    {
        return (await axios.patch(`admin/cms/videos/${id}/status`, {
            status: status
        })).data;
    }

    public static async publish(id: number, model: PublishModel)
    {
        return (await axios.patch(`admin/cms/videos/${id}/publish`, model)).data;
    }

    public static async attach(id: number, pageId: number)
    {
        return (await axios.post(`admin/cms/videos/${id}/page/${pageId}`)).data;
    }

    public static async detach(id: number, pageId: number)
    {
        return (await axios.delete(`admin/cms/videos/${id}/page/${pageId}`)).data;
    }

    public static async getVersions(id: number, pager: Pager)
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/cms/videos/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    public static async getPagePublications(id: number)
    {
        return (await axios.get<PagePublication[]>(`admin/cms/videos/${id}/pages`)).data;
    }

    public static async getAutocompleteList(search: string, limit: number = 10, videoId: number = 0)
    {
        return (await axios.get<ListItemModel[]>('admin/cms/videos/autocomplete', {
            params: {
                search: search,
                limit: limit,
                videoId: videoId
            }
        })).data;
    }

    public static async fetchAutocompleteModel(id: number)
    {
        return (await axios.get<ListItemModel>(`admin/cms/videos/autocomplete/${id}`)).data;
    }

    public static async getRelatedFromWidget(id: number)
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/videos/${id}/relatedFromWidget`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    title: string;
    createdBy: string;
    datePublishedUtc: DateTime;
    dateDismissedUtc: DateTime;
    status: PublicationEnum;
    ownerId: number;
    resourceId: number;
    originId: number;
}

export interface FilterModel
{
    pageId?: number;
    userId: number;
    status: string;
    title: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    authorId?: number;
}

export interface UpsertModel
{
    id: number;
    title: string;
    description: string;
    tags: string[];
    picture: File[];
    video: File[];
    videoUrl: string;
    authorId: number;
    meta: MetaSettingsModel | null;
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
    locale: string;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}
